/**
 * 使用缓存资源的组合式函数
 * 自动管理 Blob URL 生命周期，防止内存泄漏
 */

import { ref, watch, onUnmounted, type Ref } from 'vue'
import { getCachedResourceUrl, isCacheableResource } from '@/utils/cached-resource'

/**
 * 使用缓存的资源 URL
 * @param url 原始资源 URL
 * @returns 响应式的缓存资源 URL 和控制方法
 */
export const useCachedResource = (url: Ref<string> | string) => {
  const cachedUrl = ref<string>(typeof url === 'string' ? url : url.value)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  // 保存当前 Blob URL，用于清理
  let currentBlobUrl: string | null = null

  const loadCachedResource = async (sourceUrl: string) => {
    // 清理旧的 Blob URL，防止内存泄漏
    if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentBlobUrl)
      currentBlobUrl = null
    }

    if (!isCacheableResource(sourceUrl)) {
      cachedUrl.value = sourceUrl
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const url = await getCachedResourceUrl(sourceUrl)
      // 保存 Blob URL 引用
      if (url.startsWith('blob:')) {
        currentBlobUrl = url
      }
      cachedUrl.value = url
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))
      // 失败时使用原始 URL
      cachedUrl.value = sourceUrl
    } finally {
      isLoading.value = false
    }
  }

  // 清理 Blob URL
  const cleanup = () => {
    if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentBlobUrl)
      currentBlobUrl = null
    }
  }

  if (typeof url === 'string') {
    void loadCachedResource(url)
  } else {
    // 监听 URL 变化
    watch(url, newUrl => {
      void loadCachedResource(newUrl)
    }, { immediate: true })
  }

  // 组件卸载时自动清理
  onUnmounted(() => {
    cleanup()
  })

  return {
    cachedUrl,
    isLoading,
    error,
    reload: () => {
      const sourceUrl = typeof url === 'string' ? url : url.value
      void loadCachedResource(sourceUrl)
    },
    cleanup, // 导出清理方法，供手动调用
  }
}
